/**
 * One gap a gap-analysis execution emitted, held as a DRAFT while it is
 * enriched and reviewed on its way to becoming a product-facing {@link Gap}.
 *
 * The run collection (`gap_analysis_runs`) stores a whole execution per scope
 * and is append-only bookkeeping: it answers "what did this run do". A draft
 * answers a different question — "is this one gap good enough to ship, and what
 * is still missing from it" — which needs a document per gap, because that is
 * the unit an agent enriches and a human approves.
 *
 * The document is two layers that never mix:
 *
 *   - {@link GapDraft.source} is the {@link RunGap} exactly as the run wrote it.
 *     It is FROZEN: written once at insert and never touched again, so the
 *     finder/validator's own words stay auditable no matter what later agents
 *     conclude.
 *   - {@link GapDraft.gap} is the product shape being assembled on top of it. It
 *     starts nearly empty — only what can be derived mechanically from the run —
 *     and each enrichment agent fills in the fields it owns.
 *
 * Delivery and sales share one collection; {@link GapDraft.variant} says which
 * pipeline produced the draft, and matches `source.variant`.
 *
 * Nothing imports this yet. Like `gap_analysis_runs`, the collection is written
 * by the persist scripts — plain Node with no access to `@momentum/types` — so
 * the type and its schema are the contract those scripts are written against
 * rather than code in their path.
 */
/**
 * An enrichment agent that runs over a draft. One key per agent, so a draft
 * records what has been attempted without an ordered pipeline state. A missing
 * key means that agent has not run — absence is the only way "not run" is
 * expressed, so it cannot drift out of sync with a status value.
 *
 * There are two agents, and they own disjoint sets of `gap.*` fields.
 * `overview` writes `gap.title`, `gap.projectOverview` and `gap.escalation`;
 * `enrichment` writes the rest. Because the sets do not overlap, one agent's
 * outcome says nothing about the other's fields, and either can run — or
 * fail — without the other.
 */
export var GapDraftAgent;
(function (GapDraftAgent) {
    /** Writes the heading, the overview and the escalation timeline. */
    GapDraftAgent["OVERVIEW"] = "overview";
    /** Writes the impact paragraph, the ROI, the fix steps, the context and the three scores. */
    GapDraftAgent["ENRICHMENT"] = "enrichment";
})(GapDraftAgent || (GapDraftAgent = {}));
/**
 * How one agent's attempt ended. There is no `NOT_RUN`: an agent that never ran
 * has no entry at all, so absence is the only way "not run" is expressed and it
 * cannot drift out of sync with a status value.
 */
export var GapDraftAgentStatus;
(function (GapDraftAgentStatus) {
    GapDraftAgentStatus["RUNNING"] = "running";
    GapDraftAgentStatus["DONE"] = "done";
    GapDraftAgentStatus["FAILED"] = "failed";
})(GapDraftAgentStatus || (GapDraftAgentStatus = {}));
/** Where a draft stands with a human reviewer. */
export var GapDraftReviewStatus;
(function (GapDraftReviewStatus) {
    GapDraftReviewStatus["PENDING"] = "pending";
    GapDraftReviewStatus["APPROVED"] = "approved";
    GapDraftReviewStatus["REJECTED"] = "rejected";
})(GapDraftReviewStatus || (GapDraftReviewStatus = {}));
/**
 * Whether a status is a decision rather than the absence of one.
 *
 * `null` is how the API says "clear the verdict", and PENDING is what clearing
 * leaves behind, so the two mean the same thing here. Everything that turns on
 * "has this draft been decided" — the required
 * {@link GapDraftReviewConfidence}, the reviewer, the timestamp — asks through
 * this, so keep and drop cannot drift apart by one of them being spelled out
 * twice.
 */
export function isDecidedGapReviewStatus(status) {
    return (status === GapDraftReviewStatus.APPROVED ||
        status === GapDraftReviewStatus.REJECTED);
}
/**
 * How sure the reviewer is that their verdict — keep or drop — was the right
 * call.
 *
 * Three steps rather than the finder's five ({@link GapRunConfidence}): this is
 * a human filing a judgement mid-queue, and a scale they have to think about is
 * a scale they will fill in badly. The two are deliberately separate anyway —
 * one is the pipeline's confidence that the gap is REAL, this is a reviewer's
 * confidence in what they decided to do about it.
 */
export var GapDraftReviewConfidence;
(function (GapDraftReviewConfidence) {
    GapDraftReviewConfidence["LOW"] = "low";
    GapDraftReviewConfidence["MEDIUM"] = "medium";
    GapDraftReviewConfidence["HIGH"] = "high";
})(GapDraftReviewConfidence || (GapDraftReviewConfidence = {}));
/**
 * The level the reviewer's picker opens on, on a draft that has none recorded.
 *
 * Exported because the client presets its control with it — the value is
 * REQUIRED on the wire, so "the default" is a thing the UI supplies rather than
 * something the API fills in behind a caller's back.
 */
export const DEFAULT_GAP_REVIEW_CONFIDENCE = GapDraftReviewConfidence.MEDIUM;
/**
 * Whether the enrichment agent produced an ROI figure, or deliberately did not.
 *
 * There is no third value and no "unknown": the agent accounts for the figure
 * either way, so a draft whose {@link GapDraft.roiDerivation} is absent predates
 * the field rather than being one the agent had nothing to say about.
 */
export var GapRoiDerivationOutcome;
(function (GapRoiDerivationOutcome) {
    GapRoiDerivationOutcome["CALCULATED"] = "calculated";
    GapRoiDerivationOutcome["SKIPPED"] = "skipped";
})(GapRoiDerivationOutcome || (GapRoiDerivationOutcome = {}));
